import type { RefObject } from "react";
import { IconSearch } from "@tabler/icons-react";
import { Badge } from "@/components/lilia-ui/badge";
import { PaperGlyph, SheetLabel, TableGlyph, sheetRowStyle } from "./InsertSheetParts";
import { predictTableNumber, sectionLabel, type PaperSection } from "./paperOutline";
import { splitRecent } from "./recentTables";
import { editedAgo, tableName } from "./tableWords";
import type { BlockPlace, Paper, TableLink } from "./usePaper";
import type { DocSummary } from "./useTableInsert";
import type { TableSummary } from "./useTables";
import { testIds } from "@/testIds";

/**
 * 1b, pick a paper, then a place: the same sheet, mirrored. The right side is
 * the paper's outline, with a dashed "Lands here" slot where the table goes.
 */

export const paperDomId = (id: string) => `paper-insert-option-${id}`;

/** Recent papers first: the last opened, then the last edited. */
export function paperOrder(docs: DocSummary[], query: string): { recent: DocSummary[]; rest: DocSummary[] } {
  const when = (d: DocSummary) => new Date(d.lastOpenedAt ?? d.updatedAt ?? 0).getTime() || 0;
  const sorted = [...docs].sort((a, b) => when(b) - when(a));
  if (query.trim()) return { recent: [], rest: sorted };
  return splitRecent(sorted, []);
}

export function PaperList({
  table, recent, rest, loading, chosen, onChoose, onCommit, query, onQuery, searchRef, listRef, links, places,
}: {
  table: TableSummary;
  recent: DocSummary[];
  rest: DocSummary[];
  loading: boolean;
  chosen: string | null;
  onChoose: (id: string) => void;
  onCommit: () => void;
  query: string;
  onQuery: (q: string) => void;
  searchRef: RefObject<HTMLInputElement | null>;
  listRef: RefObject<HTMLDivElement | null>;
  /** The papers already using this table. */
  links: TableLink[];
  places: Record<string, BlockPlace>;
}) {
  const using = new Set(links.map((l) => l.documentId));
  const count = recent.length + rest.length;

  const row = (d: DocSummary) => {
    const on = d.id === chosen;
    const already = using.has(d.id);
    const short = places[d.id]?.short;
    return (
      <div
        key={d.id}
        id={paperDomId(d.id)}
        role="option"
        aria-selected={on}
        data-testid={testIds.tableTool.paperRow}
        data-doc-id={d.id}
        data-row-id={d.id}
        data-already={already || undefined}
        data-selected={on || undefined}
        onClick={() => onChoose(d.id)}
        onDoubleClick={onCommit}
        className="flex cursor-pointer items-center gap-[11px] rounded-[9px] p-2.5 hover:bg-[var(--lilia-hover)]"
        style={sheetRowStyle(on)}
      >
        <PaperGlyph on={on} />
        <div className="min-w-0 flex-1">
          <div className={`truncate text-[13px] text-[color:var(--lilia-text)] ${on ? "font-semibold" : "font-medium"}`}>
            {d.title || "Untitled"}
          </div>
          {already ? (
            <div className="mt-px truncate text-[11.5px]" style={{ color: "var(--lilia-success)" }}>
              Already uses this table{short ? ` · ${short}` : ""}
            </div>
          ) : (
            <div className="mt-px truncate text-[11.5px]" style={{ color: on ? "var(--lilia-text-secondary)" : "var(--lilia-text-secondary)" }}>
              {[d.updatedAt ? `edited ${editedAgo(d.updatedAt)}` : "", d.sectionCount ? `${d.sectionCount} section${d.sectionCount === 1 ? "" : "s"}` : ""]
                .filter(Boolean).join(" · ")}
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="flex w-[400px] shrink-0 flex-col border-r border-[var(--lilia-border)]">
      <div className="flex flex-col gap-2.5 px-3.5 pb-1.5 pt-3.5">
        <div className="flex items-center gap-2.5 rounded-[9px] px-2.5 py-2" style={{ background: "var(--lilia-bg-secondary)" }}>
          <TableGlyph />
          <div className="min-w-0 flex-1">
            <div className="text-[11px] text-[color:var(--lilia-text-secondary)]">Using</div>
            <div className="truncate text-[13px] font-semibold text-[color:var(--lilia-text)]">{tableName(table)}</div>
          </div>
        </div>
        <label
          className="flex h-10 items-center gap-[9px] rounded-[9px] border px-3 focus-within:border-[var(--lilia-primary)] focus-within:shadow-[0_0_0_3px_var(--lilia-primary-subtle)]"
          style={{ background: "var(--lilia-bg)", borderColor: "var(--lilia-border-strong)" }}
        >
          <IconSearch size={15} className="shrink-0 text-[color:var(--lilia-text-muted)]" />
          <input
            ref={searchRef}
            autoFocus
            data-testid={testIds.tableTool.insertSearch}
            value={query}
            onChange={(e) => onQuery(e.target.value)}
            placeholder="Search your papers"
            aria-label="Search your papers"
            aria-controls="paper-insert-list"
            className="min-w-0 flex-1 border-0 bg-transparent p-0 text-[14px] text-[color:var(--lilia-text)] shadow-none outline-none focus-visible:outline-none placeholder:text-[color:var(--lilia-text-secondary)]"
          />
        </label>
      </div>

      <div
        ref={listRef}
        id="paper-insert-list"
        role="listbox"
        tabIndex={0}
        aria-label="Your papers"
        aria-activedescendant={chosen ? paperDomId(chosen) : undefined}
        className="flex min-h-0 flex-1 flex-col gap-0.5 overflow-y-auto px-2 pb-2 outline-none focus-visible:outline-none"
      >
        {loading ? (
          <div className="flex flex-col gap-2 p-3">
            {[0, 1, 2].map((i) => <div key={i} className="h-[46px] rounded-[9px]" style={{ background: "var(--lilia-bg-secondary)" }} />)}
          </div>
        ) : count === 0 ? (
          <div data-testid={testIds.tableTool.insertEmpty} className="px-3 py-6 text-[13px] text-[color:var(--lilia-text-secondary)]">
            {query.trim() ? `No paper matches “${query.trim()}”` : "No papers yet."}
          </div>
        ) : (
          <>
            {recent.length > 0 && <SheetLabel className="px-2 pb-1 pt-2">Recent papers</SheetLabel>}
            {recent.map(row)}
            <SheetLabel className={`px-2 pb-1 ${recent.length ? "pt-3" : "pt-2"}`}>
              {query.trim() ? `Matches · ${count}` : recent.length ? "All papers" : "Papers"}
            </SheetLabel>
            {rest.map(row)}
          </>
        )}
      </div>
    </div>
  );
}

/** "Table 3 · Figure 6", or "1 table · 1 figure" before a compile. */
function holds(s: PaperSection, paper: Paper): string {
  const named = (ids: string[], word: string) => {
    const nums = ids.map((id) => (paper.numbered ? paper.numbers[id] : null));
    if (nums.length && nums.every(Boolean)) return nums.map((n) => `${word} ${n}`);
    return ids.length ? [`${ids.length} ${word.toLowerCase()}${ids.length === 1 ? "" : "s"}`] : [];
  };
  return [...named(s.tables, "Table"), ...named(s.figures, "Figure")].join(" · ");
}

export function PaperDetail({
  paper, slot, onSlot, placing, already, goTo,
}: {
  /** Undefined while loading; null when it could not be read. */
  paper: Paper | null | undefined;
  slot: number;
  onSlot: (i: number) => void;
  /** ⇥ moved the keys to the outline: ↑↓ move the slot. */
  placing: boolean;
  /** Where this paper already uses the table, when it does. */
  already: BlockPlace | null;
  /** Link was chosen for a paper that already has it: the action is Go to. */
  goTo: boolean;
}) {
  if (paper === undefined) {
    return (
      <div className="flex min-w-0 flex-1 flex-col gap-3 px-[22px] py-[18px]" style={{ background: "var(--lilia-bg-secondary)" }}>
        <div className="h-4 w-2/3 rounded-[3px]" style={{ background: "var(--lilia-bg)" }} />
        <div className="h-[260px] rounded-[10px]" style={{ background: "var(--lilia-bg)" }} />
      </div>
    );
  }
  if (paper === null) {
    return (
      <div className="grid min-w-0 flex-1 place-items-center text-[12.5px] text-[color:var(--lilia-text-secondary)]" style={{ background: "var(--lilia-bg-secondary)" }}>
        This paper could not be read. It will land at the end.
      </div>
    );
  }

  const o = paper.outline;
  const layout = [
    paper.columns > 1 ? (paper.columns === 2 ? "two-column" : `${paper.columns} columns`) : "one column",
    `${o.sections.length} section${o.sections.length === 1 ? "" : "s"}`,
    `${o.tableCount} table${o.tableCount === 1 ? "" : "s"}`,
  ].join(" · ");
  const current = o.slots[Math.min(slot, o.slots.length - 1)];
  const knownTables = o.sections.flatMap((s) => s.tables).map((id) => paper.numbers[id]);
  const predicted = current ? predictTableNumber(current, paper.numbered, knownTables) : null;
  const where = current && current.after >= 0 ? `end of ${sectionLabel(o.sections[current.after])}` : "end of the document";

  const landing = (
    <div
      data-testid={testIds.tableTool.insertLanding}
      data-slot={slot}
      className="lilia-landing-slot flex h-[34px] items-center gap-2.5 px-3"
      style={{
        margin: o.sections.length ? "2px 8px 4px 36px" : "2px 8px",
        borderRadius: 7,
        border: "1.5px dashed var(--lilia-primary)",
        background: "var(--lilia-primary-subtle)",
      }}
    >
      <TableGlyph size="slot" />
      <span className="text-[12.5px] font-semibold text-[color:var(--lilia-text)]">Lands here</span>
      <span className="truncate text-[12px] text-[color:var(--lilia-text-secondary)]">
        {where}, as Table {predicted ?? "▯"}
      </span>
    </div>
  );

  return (
    <div className="flex min-w-0 flex-1 flex-col gap-3 overflow-y-auto px-[22px] py-[18px]" style={{ background: "var(--lilia-bg-secondary)" }}>
      <div className="flex items-baseline gap-2.5">
        <span className="min-w-0 truncate text-[16px] font-semibold text-[color:var(--lilia-text)]">{paper.title}</span>
        {paper.docClass && <Badge variant="outline" className="shrink-0 self-center font-medium">{paper.docClass}</Badge>}
        <div className="flex-1" />
        <span className="shrink-0 text-[11.5px] text-[color:var(--lilia-text-secondary)]">{layout}</span>
      </div>

      {already ? (
        <div className="text-[12px]" style={{ color: "var(--lilia-success)" }}>
          Already uses this table{already.where ? ` · ${already.where}` : ""}.
          {goTo ? " Go there, or choose Copy to add a separate one." : " A copy goes in as a table of its own."}
        </div>
      ) : (
        <div className="text-[12px] text-[color:var(--lilia-text-secondary)]">
          Choose where it goes. Click between sections, or use{" "}
          <span style={{ fontFamily: "var(--lilia-font-mono)" }}>↑↓</span> after{" "}
          <span style={{ fontFamily: "var(--lilia-font-mono)" }}>⇥</span>.
        </div>
      )}

      <div
        data-placing={placing || undefined}
        className="flex flex-col rounded-[10px] border px-2 py-2.5"
        style={{
          background: "var(--lilia-bg)",
          borderColor: placing ? "var(--lilia-primary)" : "var(--lilia-border)",
          boxShadow: placing ? "0 0 0 3px var(--lilia-primary-subtle)" : undefined,
        }}
      >
        {o.sections.length === 0 ? (
          <>
            <div className="px-3 py-2 text-[12.5px] text-[color:var(--lilia-text-secondary)]">This paper has no sections yet.</div>
            {!goTo && landing}
          </>
        ) : (
          o.sections.map((s, i) => (
            <div key={s.id}>
              <button
                type="button"
                data-testid={testIds.tableTool.paperSection}
                data-section={i}
                onClick={() => onSlot(i)}
                className="flex w-full items-center gap-3 rounded-[7px] px-3 py-2 text-left hover:bg-[var(--lilia-hover)]"
              >
                <span className="w-[18px] shrink-0 text-[12px] text-[color:var(--lilia-text-secondary)]" style={{ fontFamily: "var(--lilia-font-mono)" }}>
                  {s.number}
                </span>
                <span className="min-w-0 flex-1 truncate text-[13.5px] text-[color:var(--lilia-text)]" style={{ fontFamily: "var(--lilia-font-serif)" }}>
                  {s.title}
                </span>
                <span className="shrink-0 text-[11px] text-[color:var(--lilia-text-secondary)]">{holds(s, paper)}</span>
              </button>
              {!goTo && current?.after === i && landing}
            </div>
          ))
        )}
      </div>
    </div>
  );
}
